import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, Pencil, Trash, Package, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUS_THEME = {
  fresh:    { dot: "bg-emerald-500", text: "text-emerald-700", bg: "bg-emerald-50",  border: "border-emerald-200", label: "Fresh" },
  soon:     { dot: "bg-stone-400",   text: "text-stone-700",   bg: "bg-stone-50",    border: "border-stone-200",   label: "OK" },
  expiring: { dot: "bg-amber-500",   text: "text-amber-800",   bg: "bg-amber-50",    border: "border-amber-300",   label: "Expiring <30d" },
  expired:  { dot: "bg-rose-600",    text: "text-rose-800",    bg: "bg-rose-50",     border: "border-rose-300",    label: "EXPIRED" },
  unknown:  { dot: "bg-stone-300",   text: "text-stone-500",   bg: "bg-stone-50",    border: "border-stone-200",   label: "No expiry" },
};

function daysUntil(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  const today = new Date(); today.setHours(0,0,0,0);
  return Math.round((d - today) / 86400000);
}

function LotEditor({ ingredientId, supplierSpecId, lot, onSaved, onCanceled }) {
  const [lotNumber, setLotNumber] = useState(lot?.lot_number || "");
  const [received, setReceived] = useState(lot?.received_date?.slice(0, 10) || "");
  const [expiration, setExpiration] = useState(lot?.expiration_date?.slice(0, 10) || "");
  const [qtyReceived, setQtyReceived] = useState(lot?.qty_lb_received ?? "");
  const [qtyRemaining, setQtyRemaining] = useState(lot?.qty_lb_remaining ?? "");
  const [coaUrl, setCoaUrl] = useState(lot?.coa_url || "");
  const [notes, setNotes] = useState(lot?.notes || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!lotNumber.trim()) { toast.error("Lot number required"); return; }
    setSaving(true);
    const payload = {
      supplier_spec_id: supplierSpecId || null,
      lot_number: lotNumber.trim(),
      received_date: received || null,
      expiration_date: expiration || null,
      qty_lb_received: qtyReceived === "" ? null : Number(qtyReceived),
      qty_lb_remaining: qtyRemaining === "" ? null : Number(qtyRemaining),
      coa_url: coaUrl.trim(),
      notes: notes.trim(),
    };
    try {
      if (lot?.id) {
        await api.put(`/ingredients/${ingredientId}/lots/${lot.id}`, payload);
        toast.success("Lot updated");
      } else {
        await api.post(`/ingredients/${ingredientId}/lots`, payload);
        toast.success("Lot added");
      }
      onSaved();
    } catch (err) {
      console.error(err);
      toast.error("Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-3" data-testid="lot-editor">
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-stone-500">Lot number *</Label>
          <Input value={lotNumber} onChange={(e) => setLotNumber(e.target.value)}
            placeholder="LOT-2026-0142"
            className="h-8 mt-1 font-mono text-[12px]" data-testid="lot-number-input" />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-stone-500">COA URL (optional)</Label>
          <Input value={coaUrl} onChange={(e) => setCoaUrl(e.target.value)}
            placeholder="https://…"
            className="h-8 mt-1 text-[12px]" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-stone-500">Received</Label>
          <Input type="date" value={received} onChange={(e) => setReceived(e.target.value)}
            className="h-8 mt-1 text-[12px]" />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-stone-500">Expiration</Label>
          <Input type="date" value={expiration} onChange={(e) => setExpiration(e.target.value)}
            className="h-8 mt-1 text-[12px]" data-testid="lot-expiration-input" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-stone-500">Qty received (lb)</Label>
          <Input type="number" step="0.1" value={qtyReceived}
            onChange={(e) => { setQtyReceived(e.target.value); if (qtyRemaining === "") setQtyRemaining(e.target.value); }}
            className="h-8 mt-1 text-[12px]" />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-stone-500">Qty remaining (lb)</Label>
          <Input type="number" step="0.1" value={qtyRemaining}
            onChange={(e) => setQtyRemaining(e.target.value)}
            className="h-8 mt-1 text-[12px]" />
        </div>
      </div>
      <div>
        <Label className="text-[10px] uppercase tracking-wider text-stone-500">Notes</Label>
        <Input value={notes} onChange={(e) => setNotes(e.target.value)}
          placeholder="Allergen-free run? Kosher cert? Storage location?"
          className="h-8 mt-1 text-[12px]" />
      </div>
      <div className="flex gap-2 justify-end pt-1">
        <Button variant="ghost" size="sm" onClick={onCanceled} disabled={saving}>Cancel</Button>
        <Button size="sm" onClick={save} disabled={saving}
          className="bg-brand-800 hover:bg-brand-700 text-white"
          data-testid="save-lot-btn">
          {saving ? "Saving…" : (lot?.id ? "Update lot" : "Add lot")}
        </Button>
      </div>
    </div>
  );
}

function LotManagerDialog({ ingredientId, ingredientName, suppliers, lots, onChange, trigger }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null | "new" | lot_id
  const [filterSupplierId, setFilterSupplierId] = useState("");

  const reload = async () => {
    onChange();
    setEditing(null);
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this lot?")) return;
    await api.delete(`/ingredients/${ingredientId}/lots/${id}`);
    toast.success("Lot deleted");
    onChange();
  };

  const supplierName = (sid) => suppliers.find((s) => s.id === sid)?.supplier_name || "—";

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="lot-manager-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package size={18} weight="duotone" className="text-brand-700" />
            Lot inventory · {ingredientName}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div className="text-[12px] text-stone-600 leading-relaxed bg-stone-50 border border-stone-200 rounded px-3 py-2">
            Track every incoming-material lot you receive. Lots flagged <b className="text-rose-700">EXPIRED</b> in red,
            <b className="text-amber-700"> expiring within 30 days</b> in amber. FIFO order — oldest expiry first.
          </div>

          {editing === "new" ? (
            <div className="bg-brand-50 border border-brand-200 rounded-md p-3">
              <LotEditor
                ingredientId={ingredientId}
                supplierSpecId={filterSupplierId || null}
                onSaved={reload}
                onCanceled={() => setEditing(null)}
              />
            </div>
          ) : (
            <Button
              variant="outline" size="sm"
              onClick={() => setEditing("new")}
              className="w-full border-dashed border-stone-300 text-stone-600 hover:border-brand-500 hover:text-brand-800"
              data-testid="add-lot-btn">
              <Plus size={12} weight="bold" className="mr-1" /> Add new lot
            </Button>
          )}

          {lots.length === 0 && editing !== "new" ? (
            <div className="text-center text-sm text-stone-500 py-6">
              No lots tracked yet. Add the lot # printed on your supplier&apos;s certificate of analysis.
            </div>
          ) : (
            <div className="space-y-2">
              {lots.map((l) => {
                const t = STATUS_THEME[l.status] || STATUS_THEME.unknown;
                const days = daysUntil(l.expiration_date);
                if (editing === l.id) {
                  return (
                    <div key={l.id} className="bg-brand-50 border border-brand-200 rounded-md p-3">
                      <LotEditor
                        ingredientId={ingredientId}
                        supplierSpecId={l.supplier_spec_id}
                        lot={l}
                        onSaved={reload}
                        onCanceled={() => setEditing(null)}
                      />
                    </div>
                  );
                }
                return (
                  <div key={l.id} className={`rounded-md border ${t.border} ${t.bg} px-3 py-2`} data-testid={`lot-row-${l.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <span className={`h-2 w-2 rounded-full ${t.dot}`} />
                        <div>
                          <div className="font-mono font-semibold text-[13px]">{l.lot_number}</div>
                          <div className="text-[11px] text-stone-600">
                            {supplierName(l.supplier_spec_id)}
                            {l.qty_lb_remaining != null && ` · ${l.qty_lb_remaining} lb on hand`}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className={`text-[10px] uppercase tracking-wider font-bold ${t.text}`}>
                          {t.label}{days != null && l.status !== "expired" ? ` · ${days}d` : ""}
                        </span>
                        <button onClick={() => setEditing(l.id)} className="text-stone-500 hover:text-brand-700"><Pencil size={11} /></button>
                        <button onClick={() => remove(l.id)} className="text-stone-500 hover:text-rose-600"><Trash size={11} /></button>
                      </div>
                    </div>
                    {(l.received_date || l.expiration_date || l.coa_url || l.notes) && (
                      <div className="mt-1 grid grid-cols-2 gap-x-3 gap-y-0.5 text-[11px] text-stone-600">
                        {l.received_date && <div>Received: {l.received_date}</div>}
                        {l.expiration_date && <div>Expiration: {l.expiration_date}</div>}
                        {l.coa_url && <div className="col-span-2 truncate"><a href={l.coa_url} target="_blank" rel="noreferrer" className="text-brand-700 underline">COA →</a></div>}
                        {l.notes && <div className="col-span-2 italic">{l.notes}</div>}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * RecipeRowLotPicker — inline supplier + lot dropdowns for a single recipe ingredient row.
 *
 * Props:
 *   ingredientId       — string
 *   ingredientName     — string
 *   value              — { supplier_spec_id, lot_id, lot_number } (current row state)
 *   onChange(next)     — callback when user picks a new supplier/lot
 */
export default function RecipeRowLotPicker({ ingredientId, ingredientName, value, onChange }) {
  const [suppliers, setSuppliers] = useState([]);
  const [lots, setLots] = useState([]);

  const refresh = () => {
    Promise.all([
      api.get(`/ingredients/${ingredientId}/suppliers`).catch(() => ({ data: [] })),
      api.get(`/ingredients/${ingredientId}/lots`).catch(() => ({ data: [] })),
    ]).then(([s, l]) => {
      setSuppliers(s.data || []);
      setLots(l.data || []);
      // Re-sync expiration status to parent for the saved lot_id (if any)
      if (value?.lot_id) {
        const found = (l.data || []).find((x) => x.id === value.lot_id);
        if (found && found.status !== value._lot_status) {
          onChange({
            supplier_spec_id: value.supplier_spec_id,
            lot_id: value.lot_id,
            lot_number: value.lot_number,
            _lot_status: found.status,
          });
        }
      }
    });
  };
  useEffect(refresh, [ingredientId]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredLots = useMemo(() => {
    if (!value?.supplier_spec_id) return lots;
    return lots.filter((l) => l.supplier_spec_id === value.supplier_spec_id);
  }, [lots, value?.supplier_spec_id]);

  const selectedLot = lots.find((l) => l.id === value?.lot_id);
  const selectedTheme = selectedLot ? STATUS_THEME[selectedLot.status] || STATUS_THEME.unknown : null;

  return (
    <div className="flex items-center gap-1" data-testid="lot-picker">
      <select
        value={value?.supplier_spec_id || ""}
        onChange={(e) => onChange({ supplier_spec_id: e.target.value || null, lot_id: null, lot_number: null, _lot_status: null })}
        className="h-7 text-[11px] border border-stone-300 rounded bg-white px-1 max-w-[110px] truncate"
        data-testid="row-supplier-select"
      >
        <option value="">Any supplier</option>
        {suppliers.map((s) => (
          <option key={s.id} value={s.id}>{s.supplier_name}</option>
        ))}
      </select>

      <select
        value={value?.lot_id || ""}
        onChange={(e) => {
          const lot = lots.find((l) => l.id === e.target.value);
          onChange({
            supplier_spec_id: lot?.supplier_spec_id || value?.supplier_spec_id || null,
            lot_id: lot?.id || null,
            lot_number: lot?.lot_number || null,
            _lot_status: lot?.status || null,
          });
        }}
        className={`h-7 text-[11px] border rounded px-1 max-w-[120px] truncate ${
          selectedLot && selectedLot.status === "expired"
            ? "border-rose-500 bg-rose-50 text-rose-900 font-semibold"
            : selectedLot && selectedLot.status === "expiring"
            ? "border-amber-400 bg-amber-50 text-amber-900"
            : "border-stone-300 bg-white"
        }`}
        data-testid="row-lot-select"
      >
        <option value="">No lot</option>
        {filteredLots.map((l) => {
          const t = STATUS_THEME[l.status] || STATUS_THEME.unknown;
          return (
            <option key={l.id} value={l.id}>
              {l.lot_number} {l.expiration_date ? `· exp ${l.expiration_date.slice(2, 10)}` : ""} {t.label === "EXPIRED" ? "⚠" : ""}
            </option>
          );
        })}
      </select>

      {selectedLot && selectedLot.status === "expired" && (
        <Warning size={14} weight="fill" className="text-rose-600" data-testid="lot-expired-warning" />
      )}

      <LotManagerDialog
        ingredientId={ingredientId}
        ingredientName={ingredientName}
        suppliers={suppliers}
        lots={lots}
        onChange={refresh}
        trigger={
          <button
            type="button"
            className="h-7 px-1.5 text-[10px] uppercase tracking-wider font-bold text-stone-500 hover:text-brand-700 border border-stone-300 rounded bg-white hover:border-brand-500"
            data-testid="manage-lots-btn"
            title="Manage lot inventory"
          >
            Lots
          </button>
        }
      />
    </div>
  );
}
